import React from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import PasswordEditor from '../components/password-editor-view';
import environment from 'lib/environment';
import {
  meData,
  getGetGoSchema,
  meRequestPatchWithAuth,
  mePasswordLoginEnabled,
  meIsLoading,
} from 'modules/me-module';
import {uiShowToastrMessage} from 'modules/ui-module';
import {
  socialDisconnectLastAccount,
  socialDisconnectedAccountName,
  setModeDisconnectLastSocialAccount,
  setDisconnectedSocialAccountName,
  socialProviderByUserIdDelete,
} from 'modules/social-module';
import appconfig from 'config/appconfig';
import {
  getSocialAccountName,
  getForgotPasswordUrl,
} from 'lib/utility-functions';
import Session from 'lib/session';
import {setHandledErrorCodes} from 'lib/error-message-handling';
import { Base64 } from 'js-base64';

const PASSWORD_MIN_LENGTH = 8;
const PASSWORD_MAX_LENGTH = 32;

export class PasswordEditorContainer extends React.Component {
  constructor(props, context) {
    super(props, context);
    this.state = {
      personData: Object.assign({}, this.props.personData),
      errors: {},
      isDirty: false,
      modalIsOpen: false,
      lettersNumbersReqMet: false,
      minCharacterCountReqMet: false,
      newPassword: '',
      currentPassword: '',
      confirmPassword: '',
      viewPasswordAsText: {
        newPassword: false,
        currentPassword: false,
        confirmPassword: false,
      },
      enteredPasswordsMatch: false,
      socialLinksToDisplay: {},
      passwordEditorDisplayClass: '',
      allSetClass: 'hidden',
      showAllSetTitle: false,
    };
  }

  validateUserInput = (
    field,
    value,
    onBlurCheck,
  ) => {
    const stateChanges = {};
    let blnContinue = true;
    let lettersMatch = false;
    let numbersMatch = false;
    let specialCharactersMatch = false;
    let characterClassCount = 0;
    let minCharacterCount = false;
    let passwordMismatch = false;

    stateChanges.errors = {...this.state.errors};
    if (stateChanges.errors[field]) {
      delete stateChanges.errors[field];
    }

    if (
      onBlurCheck &&
      value.length &&
      (value.length < PASSWORD_MIN_LENGTH || value.length > PASSWORD_MAX_LENGTH)
    ) {
      stateChanges.errors[field] = 'password-editor.validation-error-password';
      blnContinue = false;
    }

    if (blnContinue && field === 'newPassword') {
      lettersMatch = value.match(/[A-Za-z]/);
      if (lettersMatch !== null) {
        characterClassCount++;
      }
      numbersMatch = value.match(/\d/);
      if (numbersMatch !== null) {
        characterClassCount++;
      }
      specialCharactersMatch = value.match(/[\W_]/);
      if (specialCharactersMatch !== null) {
        characterClassCount++;
      }
      minCharacterCount = value.length >= PASSWORD_MIN_LENGTH;
      passwordMismatch = false;
      if (this.state.confirmPassword.length &&
        value !== this.state.confirmPassword) {
        passwordMismatch = true;
      }
      stateChanges['lettersNumbersReqMet'] = characterClassCount > 1;
      stateChanges['minCharacterCountReqMet'] = minCharacterCount;
    }

    if (blnContinue && field === 'confirmPassword') {
      passwordMismatch = false;
      if (this.state.newPassword.length >= PASSWORD_MIN_LENGTH &&
        value.length === this.state.newPassword.length &&
        value !== this.state.newPassword) {
        passwordMismatch = true;
      }
    }

    if (blnContinue && passwordMismatch) {
      stateChanges.errors['confirmPassword'] = 'password-editor.mismatched-passwords';
    } else {
      delete stateChanges.errors['confirmPassword'];
    }

    this.setState(stateChanges);

    return blnContinue;
  };

  openModal = (e) => {
    if (typeof e !== 'undefined') {
      e.stopPropagation();
      e.preventDefault();
    }
    this.setState({modalIsOpen: true});
  };

  clearSocialAccountSettings = () => {
    this.props.setModeDisconnectLastSocialAccount('');
    this.props.setDisconnectedSocialAccountName('');
  }

  clearSocialAccountCookieAndCloseModal = (e) => {
    if (typeof e !== 'undefined') {
      e.stopPropagation();
      e.preventDefault();
    }
    Session.removeItem(appconfig.storage.disconnectSocialAccount);
    this.closeModal();
  }

  closeModal = (e) => {
    if (this.props.disconnectedSocialAccountName.length && this.state.allSetClass !== '') {
      this.closeAllSetModal(e);
    }
    if (typeof e !== 'undefined') {
      e.stopPropagation();
      e.preventDefault();
    }
    this.resetPasswordEntryFields();
    this.toggleAllSetContent(false);
    this.clearSocialAccountSettings();
    this.setState({modalIsOpen: false});
  };

  closeAllSetModal = (e) => {
    if (typeof e !== 'undefined') {
      e.stopPropagation();
      e.preventDefault();
    }
    this.clearSocialAccountSettings();
    this.toggleAllSetContent(false);
    this.closeModal();
  };

  toggleAllSetContent = (showAllSet) => {
    const passwordEditorDisplayClass = showAllSet ? 'hidden' : '';
    const allSetClass = showAllSet ? '' : 'hidden';
    const showAllSetTitle = showAllSet && this.props.disconnectedSocialAccountName.length > 0;
    this.setState({passwordEditorDisplayClass, allSetClass, showAllSetTitle});
  };

  componentDidMount() {
    const socialAccount = this.props.disconnectLastSocialAccount;
    if (socialAccount.length) {
      const linksToDisplay = {};
      if (socialAccount !== appconfig.socialAccounts.LASTPASS) {
        linksToDisplay[appconfig.socialAccounts.LASTPASS] = this.props.socialSignInLinks[appconfig.socialAccountLinks.LASTPASS];
      }
      if (socialAccount !== appconfig.socialAccounts.GOOGLE) {
        linksToDisplay[appconfig.socialAccounts.GOOGLE] = this.props.socialSignInLinks[appconfig.socialAccountLinks.GOOGLE];
      }
      if (socialAccount !== appconfig.socialAccounts.FACEBOOK) {
        linksToDisplay[appconfig.socialAccounts.FACEBOOK] = this.props.socialSignInLinks[appconfig.socialAccountLinks.FACEBOOK];
      }
      if (socialAccount !== appconfig.socialAccounts.LINKEDIN) {
        linksToDisplay[appconfig.socialAccounts.LINKEDIN] = this.props.socialSignInLinks[appconfig.socialAccountLinks.LINKEDIN];
      }
      if (socialAccount !== appconfig.socialAccounts.MICROSOFT) {
        linksToDisplay[appconfig.socialAccounts.MICROSOFT] = this.props.socialSignInLinks[appconfig.socialAccountLinks.MICROSOFT];
      }
      if (socialAccount !== appconfig.socialAccounts.APPLE) {
        linksToDisplay[appconfig.socialAccounts.APPLE] = this.props.socialSignInLinks[appconfig.socialAccountLinks.APPLE];
      }
      this.setState({socialLinksToDisplay: Object.assign({}, linksToDisplay)});
      this.openModal();
    }
  }

  componentDidUpdate = (prevProps) =>{
    const linksToDisplay = {};
    const { disconnectLastSocialAccount, socialSignInLinks } = this.props;
    const prevDisconnectLastSocialAccount = prevProps.disconnectLastSocialAccount;
    // Only update list when the modal is not open yet.
    //   Otherwise, a previous Toastr message can update the state which will in effect the removal of the links from view unexpectedly.
    if (!this.state.modalIsOpen) {
      if (disconnectLastSocialAccount !== prevDisconnectLastSocialAccount &&
        disconnectLastSocialAccount.length) {
        if (disconnectLastSocialAccount !== appconfig.socialAccounts.LASTPASS) {
          linksToDisplay[appconfig.socialAccounts.LASTPASS] = socialSignInLinks[appconfig.socialAccountLinks.LASTPASS];
        }
        if (disconnectLastSocialAccount !== appconfig.socialAccounts.GOOGLE) {
          linksToDisplay[appconfig.socialAccounts.GOOGLE] = socialSignInLinks[appconfig.socialAccountLinks.GOOGLE];
        }
        if (disconnectLastSocialAccount !== appconfig.socialAccounts.FACEBOOK) {
          linksToDisplay[appconfig.socialAccounts.FACEBOOK] = socialSignInLinks[appconfig.socialAccountLinks.FACEBOOK];
        }
        if (disconnectLastSocialAccount !== appconfig.socialAccounts.LINKEDIN) {
          linksToDisplay[appconfig.socialAccounts.LINKEDIN] = socialSignInLinks[appconfig.socialAccountLinks.LINKEDIN];
        }
        if (disconnectLastSocialAccount !== appconfig.socialAccounts.MICROSOFT) {
          linksToDisplay[appconfig.socialAccounts.MICROSOFT] = socialSignInLinks[appconfig.socialAccountLinks.MICROSOFT];
        }
        if (disconnectLastSocialAccount !== appconfig.socialAccounts.APPLE) {
          linksToDisplay[appconfig.socialAccounts.APPLE] = socialSignInLinks[appconfig.socialAccountLinks.APPLE];
        }
        this.openModal();
      }

      if(linksToDisplay[appconfig.socialAccounts.GOOGLE] ||
        linksToDisplay[appconfig.socialAccounts.FACEBOOK] ||
        linksToDisplay[appconfig.socialAccounts.LINKEDIN] ||
        linksToDisplay[appconfig.socialAccounts.MICROSOFT] ||
        linksToDisplay[appconfig.socialAccounts.LASTPASS] ||
        linksToDisplay[appconfig.socialAccounts.APPLE]) {
        this.setState({socialLinksToDisplay: Object.assign({}, linksToDisplay)});
      }
    }
  };

  resetPasswordEntryFields = () => {
    this.setState(
      {
        newPassword: '',
        currentPassword: '',
        isDirty: false,
        confirmPassword: '',
        viewPasswordAsText: {
          newPassword: false,
          currentPassword: false,
          confirmPassword: false,
        },
        enteredPasswordsMatch: false,
        lettersNumbersReqMet: false,
        minCharacterCountReqMet: false,
        errors: {},
      },
    );
  };

  onBlur = (e) => {
    const field = (e.target.name || e.target.id).split('-')[0];
    const value = e.target.value;
    this.togglePasswordTextViews();
    return this.validateUserInput(field, value, true);
  };

  onChange = (e) => {
    const data = {...this.state};
    const field = (e.target.name || e.target.id).split('-')[0];
    const value = e.target.value;
    if (field === 'currentPassword' && // reset currentPassword error state onchange if it is a result of a server-side rejection (not a validation error);
      data.errors.currentPassword &&
      data.errors.currentPassword === 'password-editor.save-password-failed') {
      delete data.errors.currentPassword;
    }
    data.isDirty = true;
    if (this.validateUserInput(field, value, false)) {
      data[field] = value;
    }
    this.setState(data);
    return true;
  };

  onFocus = (e) => {
    const field = (e.target.name || e.target.id).split('-')[0];
    this.togglePasswordTextViews(field, true);
    return true;
  };

  displayToastrMessage = (toastrType, toastrMessageId) => {
    this.props.uiShowToastrMessage(toastrType, toastrMessageId);
  };

  storeSocialAccountName = () => {
    this.props.setDisconnectedSocialAccountName(getSocialAccountName(this.props.disconnectLastSocialAccount));
  };

  onSave = () => {
    const socialAccountChange = this.props.disconnectLastSocialAccount.length;
    if (socialAccountChange) { this.storeSocialAccountName(); }
    if ((!this.props.passwordSet && !this.state.currentPassword.length && this.state.newPassword.length) ||
      (this.props.passwordSet && this.state.currentPassword.length && this.state.newPassword.length) ||
      (socialAccountChange && this.state.newPassword.length)) {
      setHandledErrorCodes([
        'invalid.credentials',
        'password.reuse',
      ]);

      return this.props.meRequestPatchWithAuth(`B64Password ${Base64.encode(this.state.currentPassword)}`, {password: this.state.newPassword || ''})
        .then(
          () => {
            this.displayToastrMessage('success',
              (socialAccountChange ?
                'password-editor.password-save-successful' :
                'password-editor.password-change-successful'),
            );
            return socialAccountChange ? this.props.socialProviderByUserIdDelete(this.props.personData.id, this.props.disconnectLastSocialAccount) : undefined;
          },
          (ex) => {
            if (ex &&
              ex.data.error &&
              ex.data.error.length &&
              ex.data.error.find(error => error.code === 'password.reuse')) {
                this.setState({errors: {newPassword: 'password-editor.save-password-failed-reused-password'}});
              } else {
                this.setState({errors: {currentPassword: 'password-editor.save-password-failed'}});
              }
          },
        )
        .then(
          () => {
            if (Object.keys(this.state.errors).length === 0) {
              if (socialAccountChange) {
                this.toggleAllSetContent(true);
              } else {
                this.closeModal();
              }
            }
          },
        );
    }
    return true;
  };

  passwordEditEnabled = () => (this.props.passwordLoginEnabled);

  togglePasswordTextViews = (setTextViewOn, onFocusEvent) => {
    const data = {...this.state};
    const onFocus = onFocusEvent || false;
    const switchFieldMode = typeof setTextViewOn === 'string';
    let viewMode = false;
    if (switchFieldMode) {
      viewMode = data.viewPasswordAsText[setTextViewOn];
    }
    data.viewPasswordAsText.newPassword = false;
    data.viewPasswordAsText.currentPassword = false;
    data.viewPasswordAsText.confirmPassword = false;
    if (switchFieldMode) {
      data.viewPasswordAsText[setTextViewOn] = onFocus ? viewMode : !viewMode;
    }
    this.setState(data);
  };

  onViewAsTextClick = (event) => {
    const triggerElement = event.target;
    let fieldId = triggerElement.id;
    if (!fieldId) { // if the icon 'i' element was clicked then we need to get it's parent's (span tag) id.
      fieldId = triggerElement.parentNode.id;
    }
    this.togglePasswordTextViews(fieldId.split('_')[0], false);
    return true;
  };

  onViewAsTextByKeyboard = (event) => {
    if (event.which === 13) {
      return this.onViewAsTextClick(event);
    }
    return false;
  };

  enableSubmit() {
    return (Object.keys(this.state.errors).length === 0 &&
      this.state.isDirty &&
      !this.props.isLoading &&
      (this.state.currentPassword !== '' || !this.props.passwordSet ||
        this.props.disconnectLastSocialAccount.length > 0) &&
      this.state.newPassword !== '' &&
      this.state.lettersNumbersReqMet &&
      this.state.minCharacterCountReqMet &&
      this.state.currentPassword !== this.state.newPassword &&
      this.state.newPassword === this.state.confirmPassword
    );
  }

  // currying, because function calls with params assigned to onClick events are called immediately
  // (https://www.sitepoint.com/currying-in-functional-javascript/)
  storeDisconnectedSocialConnection = () => () => {
    Session.setItem(appconfig.storage.disconnectSocialAccount, this.props.disconnectLastSocialAccount);
    return true;
  };

  render() {
    return (
      <PasswordEditor
        personData={this.state.personData}
        onEditLinkClick={this.openModal}
        modalIsOpen={this.state.modalIsOpen}
        closeModal={this.clearSocialAccountCookieAndCloseModal}
        passwordSet={this.props.passwordSet}
        passwordLoginEnabled={this.passwordEditEnabled()}
        currentPassword={this.state.currentPassword}
        newPassword={this.state.newPassword}
        confirmPassword={this.state.confirmPassword}
        forgotPasswordOnClick={getForgotPasswordUrl()}
        lettersNumbersReqMet={this.state.lettersNumbersReqMet}
        minCharacterCountReqMet={this.state.minCharacterCountReqMet}
        onViewAsTextClick={this.onViewAsTextClick}
        onViewAsTextByKeyboard={this.onViewAsTextByKeyboard}
        viewPasswordAsText={this.state.viewPasswordAsText}
        onChange={this.onChange}
        onBlur={this.onBlur}
        onSave={this.onSave}
        onFocus={this.onFocus}
        enableSubmit={this.enableSubmit()}
        isLoading={this.props.isLoading}
        errors={this.state.errors}
        disconnectLastSocialAccount={this.props.disconnectLastSocialAccount.length > 0}
        disconnectedSocialAccountName={this.props.disconnectedSocialAccountName}
        socialLinksToDisplay={this.state.socialLinksToDisplay}
        closeAllSetModal={this.closeAllSetModal}
        passwordEditorDisplayClass={this.state.passwordEditorDisplayClass}
        allSetClass={this.state.allSetClass}
        showAllSetTitle={this.state.showAllSetTitle}
        storeDisconnectedSocialConnection={this.storeDisconnectedSocialConnection}
      />
    );
  }
}

PasswordEditorContainer.propTypes = {
  passwordSet: PropTypes.bool.isRequired,
  passwordLoginEnabled: PropTypes.bool.isRequired,
  isLoading: PropTypes.bool.isRequired,
  meRequestPatchWithAuth: PropTypes.func.isRequired,
  uiShowToastrMessage: PropTypes.func.isRequired,
  disconnectLastSocialAccount: PropTypes.string.isRequired,
  setModeDisconnectLastSocialAccount: PropTypes.func.isRequired,
  setDisconnectedSocialAccountName: PropTypes.func.isRequired,
  disconnectedSocialAccountName: PropTypes.string.isRequired,
  personData: PropTypes.object.isRequired,
  socialSignInLinks: PropTypes.object.isRequired,
  socialProviderByUserIdDelete: PropTypes.func.isRequired,
};

export const mapStateToProps = (state) => {
  let passwordSet = false;
  const getGoSchema = getGetGoSchema(state);
  if (getGoSchema && getGoSchema.passwordSet) {
    passwordSet = getGoSchema.passwordSet;
  }

  return {
    personData: meData(state),
    passwordSet,
    passwordLoginEnabled: mePasswordLoginEnabled(state),
    isLoading: meIsLoading(state),
    disconnectLastSocialAccount: socialDisconnectLastAccount(state),
    disconnectedSocialAccountName: socialDisconnectedAccountName(state),
  };
};

export const mapDispatchToProps = (dispatch) => ({
  meRequestPatchWithAuth: (passwordAuth, payload) => dispatch(meRequestPatchWithAuth(passwordAuth, payload)),
  uiShowToastrMessage: (toastrType, toastrMessageId) => dispatch(uiShowToastrMessage(toastrType, toastrMessageId)),
  socialProviderByUserIdDelete: (userId, socialAccount) => dispatch(socialProviderByUserIdDelete(userId, socialAccount)),
  setModeDisconnectLastSocialAccount: (socialAccount) => dispatch(setModeDisconnectLastSocialAccount(socialAccount)),
  setDisconnectedSocialAccountName: (socialAccount) => dispatch(setDisconnectedSocialAccountName(socialAccount)),
});

export default connect(mapStateToProps, mapDispatchToProps)(PasswordEditorContainer);
